import { useEffect, useMemo, useState } from 'react'

import { useGetQuote } from 'components/hooks/useGetQuote'
import QuoteFlowHeader from 'components/quoting/QuoteFlowHeader'
import { type QuoteGenerationOptions, type SuggestedBenchmark } from 'components/quoting/types'
import { Banner, TooltipProvider } from 'slab'

import CustomizeBenchmarks from './CustomizeBenchmarks'
import PeriodToggle from './PeriodToggle'
import SuggestionCard from './SuggestionCard'

type Props = {
  prospectId: string
  quoteId: string
  canCustomizeBenchmarks?: boolean
  quoteGenerationOptions: QuoteGenerationOptions
  prospectName: string
  nextStepPath: string
  previousStepPath: string
  processingQuotePath: string
  defaultEmployerSpouseContribution: number
  defaultEmployerChildContribution: number
}

const benchmarkCostKey = (benchmark: SuggestedBenchmark) => `${benchmark.metal_tier}-${benchmark.coverage_percentile}`

export function BenchmarkSuggestions ({
  prospectId,
  quoteId,
  canCustomizeBenchmarks,
  quoteGenerationOptions,
  prospectName,
  nextStepPath,
  processingQuotePath,
  defaultEmployerSpouseContribution,
  defaultEmployerChildContribution,
}: Props) {
  const [quoteProcessing, setQuoteProcessing] = useState(true)
  const [error, setError] = useState('')
  const [benchmarkCosts, setBenchmarkCosts] = useState<Record<string, number>>({})
  const [period, setPeriod] = useState<'monthly' | 'annual'>('annual')

  const { data: quote } = useGetQuote({
    prospectId,
    quoteId,
    refetchUntilProcessed: true,
  })

  useEffect(() => {
    if (quote?.status === 'failed') {
      setQuoteProcessing(false)
      setError(quote.quote_errors?.join(', ') ?? 'Please return to the previous step and try again.')
    } else if (quote?.status === 'processed') {
      setQuoteProcessing(false)
    }
  }, [quote])

  const benchmarkDataLoaded = useMemo(() => {
    return Object.keys(benchmarkCosts).length === quoteGenerationOptions.suggested_benchmarks.length
  }, [benchmarkCosts, quoteGenerationOptions.suggested_benchmarks])

  const sortedBenchmarks = useMemo(() => {
    if (!benchmarkDataLoaded) {
      return quoteGenerationOptions.suggested_benchmarks
    }

    return quoteGenerationOptions.suggested_benchmarks.sort((a, b) => {
      return benchmarkCosts[benchmarkCostKey(a)] - benchmarkCosts[benchmarkCostKey(b)]
    })
  }, [quoteGenerationOptions.suggested_benchmarks, benchmarkCosts, benchmarkDataLoaded])

  const onResultsLoaded = (benchmark: SuggestedBenchmark, totalCost: number) => {
    const key = benchmarkCostKey(benchmark)
    setBenchmarkCosts((prev) => ({ ...prev, [key]: totalCost }))
  }

  return (
    <div className='flex justify-center'>
      <div className='flex flex-col w-full'>
        <QuoteFlowHeader
          title={`${prospectName || 'Suggested'} Quote options`}
          description='These estimates may vary based on the plan choices your employees make and actual participation numbers.'
          trailingTitle={
            <div className='w-1/3 min-w-48'>
              <PeriodToggle
                period={period}
                onChange={(period) => setPeriod(period)}
              />
            </div>
          }
        />
        <div className='grid w-full gap-10'>
          <div className='flex flex-col xl:flex-row items-center xl:items-end gap-4 flex-wrap w-full'>
            {sortedBenchmarks.map((benchmark) => (
              <div key={`${quoteId}-${benchmark.metal_tier}-${benchmark.coverage_percentile}`} className='flex-1 min-w-0'>
                <SuggestionCard
                  quoteId={quoteId}
                  quoteProcessing={quoteProcessing}
                  isLoading={quoteProcessing || !benchmarkDataLoaded}
                  tagText={benchmark.optimal ? 'Closest to your current benefits' : undefined}
                  period={period}
                  benchmark={benchmark}
                  nextStepPath={nextStepPath}
                  onResultsLoaded={(totalCost) => onResultsLoaded(benchmark, totalCost)}
                  defaultEmployerSpouseContribution={defaultEmployerSpouseContribution}
                  defaultEmployerChildContribution={defaultEmployerChildContribution}
                />
              </div>
            ))}
          </div>
          {error &&
            <Banner type='critical' title='Error generating quote' onDismiss={() => setError('')}>
              <p>
                {error}
              </p>
            </Banner>}
          <div className='flex justify-end pb-10'>
            <CustomizeBenchmarks
              prospectId={prospectId}
              quoteId={quoteId}
              canCustomizeBenchmarks={canCustomizeBenchmarks}
              quoteGenerationOptions={quoteGenerationOptions}
              processingQuotePath={processingQuotePath}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default (props: Props) => {
  return (
    <TooltipProvider>
      <BenchmarkSuggestions {...props} />
    </TooltipProvider>
  )
}
